import React, { useState, useEffect } from 'react';
import { Lightbulb, Plus, ThumbsUp, Send, Loader2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const FeatureRequestsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [topRequests, setTopRequests] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [votingId, setVotingId] = useState(null);
  
  const [newRequest, setNewRequest] = useState({ title: '', description: '', category: 'general', use_case: '' });

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/feature-requests`);
      setMyRequests(response.data.my_requests || []);
      setTopRequests(response.data.top_requests || []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newRequest.title.trim() || !newRequest.description.trim()) return toast.error('Please fill in title and description');
    setSubmitting(true);
    try {
      await axios.post(`${API}/feature-requests`, newRequest);
      toast.success('Feature request submitted!');
      setNewRequest({ title: '', description: '', category: 'general', use_case: '' });
      setShowNewForm(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (requestId) => {
    setVotingId(requestId);
    try {
      await axios.post(`${API}/feature-requests/${requestId}/vote`);
      toast.success('Vote recorded!');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    } finally {
      setVotingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-zinc-100 text-zinc-600',
      under_review: 'bg-blue-100 text-blue-700',
      planned: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-amber-100 text-amber-700',
      completed: 'bg-amber-100 text-amber-600',
      rejected: 'bg-red-100 text-red-700'
    };
    return badges[status] || badges.pending;
  };

  const categoryIcons = { ai_features: '✨', ui_ux: '🎨', export: '📤', collaboration: '👥', themes: '🎭', general: '💡' };

  const RequestCard = ({ request, showVote = true }) => {
    const hasVoted = request.voters?.includes(user?.user_id);
    return (
      <div className="p-4 bg-white rounded-lg border border-zinc-200 hover:border-amber-200 transition-all">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{categoryIcons[request.category] || '💡'}</span>
              <h4 className="text-zinc-900 font-medium">{request.title}</h4>
            </div>
            <p className="text-zinc-500 text-sm mb-2 line-clamp-2">{request.description}</p>
            <div className="flex items-center gap-3 text-xs">
              <span className={`px-2 py-0.5 rounded ${getStatusBadge(request.status)}`}>{request.status}</span>
              <span className="text-zinc-400">{new Date(request.created_at).toLocaleDateString()}</span>
              {request.user_name && <span className="text-zinc-400">by {request.user_name}</span>}
            </div>
          </div>
          {showVote && (
            <button
              onClick={() => !hasVoted && handleVote(request.request_id)}
              disabled={hasVoted || votingId === request.request_id}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
                hasVoted ? 'bg-amber-100 text-amber-600 cursor-default' : 'bg-zinc-100 text-zinc-600 hover:bg-amber-100 hover:text-amber-600'
              }`}
            >
              {votingId === request.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className={`w-4 h-4 ${hasVoted ? 'fill-amber-500' : ''}`} />}
              <span className="text-sm font-semibold">{request.votes || 0}</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title="Feature Requests" />
      
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header with New Request button */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-zinc-500 text-sm">Help shape the future of Wow!CX Design Studio.</p>
            </div>
            <Button onClick={() => setShowNewForm(!showNewForm)} className="bg-amber-500 hover:bg-amber-600" data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-2" />New Request
            </Button>
          </div>

        {/* New Request Form */}
        {showNewForm && (
          <Card className="bg-white border-zinc-200 mb-6">
            <CardHeader>
              <CardTitle className="text-zinc-900 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Submit a Feature Request
              </CardTitle>
              <CardDescription className="text-zinc-500">Tell us what would make Wow!CX better</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Title *</label>
                  <Input value={newRequest.title} onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })} placeholder="Brief title for your idea" className="bg-zinc-50 border-zinc-200" data-testid="request-title-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Category</label>
                  <select value={newRequest.category} onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })} className="w-full h-10 px-3 rounded-md bg-zinc-50 border border-zinc-200">
                    <option value="general">💡 General</option>
                    <option value="ai_features">✨ AI Features</option>
                    <option value="ui_ux">🎨 UI/UX Improvements</option>
                    <option value="export">📤 Export Options</option>
                    <option value="collaboration">👥 Team & Collaboration</option>
                    <option value="themes">🎭 Themes & Templates</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Description *</label>
                  <Textarea value={newRequest.description} onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })} placeholder="Describe the feature in detail" className="bg-zinc-50 border-zinc-200 min-h-[100px]" data-testid="request-description-input" />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="submit-request-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Submit
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowNewForm(false)} className="border-zinc-200">Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* My Requests */}
        {myRequests.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-zinc-900 mb-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />Your Requests
            </h2>
            <div className="space-y-3">{myRequests.map((req) => <RequestCard key={req.request_id} request={req} showVote={false} />)}</div>
          </div>
        )}

        {/* Top Voted */}
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />Popular Requests
          </h2>
          {loading ? (
            <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          ) : topRequests.length === 0 && myRequests.length === 0 ? (
            <Card className="bg-white border-zinc-200">
              <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                <Lightbulb className="w-12 h-12 text-zinc-300 mb-3" />
                <p className="text-zinc-600">No feature requests yet</p>
                <p className="text-zinc-400 text-sm">Be the first to suggest a new feature!</p>
                <Button onClick={() => setShowNewForm(true)} className="mt-4 bg-amber-500 hover:bg-amber-600"><Plus className="w-4 h-4 mr-2" />Submit First Request</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">{topRequests.map((req) => <RequestCard key={req.request_id} request={req} />)}</div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureRequestsPage;
